import {
    type AlignValue,
    type RichTextSpan,
    type TextBuffer,
    type TextEngine,
    type WritingModeValue,
} from '@meitu/x-design-text';
import { version as engineVersion } from '@meitu/x-design-text/package.json';
import { ceilNonNegative, isFiniteNumber, isPositiveFiniteNumber } from '@/utils/math-kit';
import { measureDelegatedHostRuns } from './host';

/**
 * render adapter 与 buffer 测量共享的最小文本输入模型。
 *
 * 字体、fallback 和 subset 生命周期由调用方负责；本模型中的 `spans` 应该已经包含文本引擎可消费的
 * `RichTextSpan` 属性。固定行内尺寸时按书写模式传入 `width` 或 `height`，自适应时测量入口不会写回图层宽高。
 *
 * @param text - string，图层的完整纯文本内容，用于空 span 兜底和列表段落计数。
 * @param fontSize - number，文本基础字号。
 * @param lineHeightScale - number，文本行高缩放。
 * @param letterSpacing - number，文本字距。
 * @param align - AlignValue，文本水平对齐方式。
 * @param spans - RichTextSpan[]，已解析字体后的引擎富文本 span。
 * @param fixedInlineSize - boolean，是否使用固定行内尺寸约束。
 * @param width - number，可选；横排固定行内尺寸。
 * @param height - number，可选；竖排固定行内尺寸。
 */
export type MeasureBufferInput = {
    text: string;
    fontSize: number;
    lineHeightScale: number;
    letterSpacing: number;
    align: AlignValue;
    spans: RichTextSpan[];
    writingMode: WritingModeValue;
    fixedInlineSize: boolean;
    width?: number;
    height?: number;
};

/**
 * buffer 测量入口的前置条件错误类型。
 */
type MeasureBufferErrorReason =
    | 'engine-not-ready'
    | 'no-registered-fonts'
    | 'invalid-font-size'
    | 'invalid-line-height-scale'
    | 'invalid-fixed-inline-size';

/**
 * 表示 buffer 测量前置条件不满足的错误。
 *
 * @param reason - MeasureBufferErrorReason，错误原因枚举。
 * @param message - string，面向调试的错误说明。
 * @returns MeasureBufferError 实例。
 */
class MeasureBufferError extends Error {
    constructor(
        readonly reason: MeasureBufferErrorReason,
        message: string,
    ) {
        super(message);
        this.name = 'MeasureBufferError';
    }
}

const Errors = {
    EngineNotReady: new MeasureBufferError('engine-not-ready', 'Text engine is not ready.'),
    NoRegisteredFonts: new MeasureBufferError('no-registered-fonts', 'Text engine has no registered fonts.'),
    InvalidFontSize: new MeasureBufferError('invalid-font-size', 'Invalid font size.'),
    InvalidLineHeightScale: new MeasureBufferError('invalid-line-height-scale', 'Invalid line height scale.'),
    InvalidFixedInlineSize: new MeasureBufferError('invalid-fixed-inline-size', 'Invalid fixed inline size.'),
};

/**
 * buffer 测量返回的文本几何信息。
 */
export type BufferMeasureResult = {
    /**
     * 测量后的外层宽度；固定宽度模式下等于输入宽度，自适应模式下等于内容宽度
     */
    width: number;
    /**
     * 正文内容高度。
     */
    height: number;
    /**
     * 最小内容行内尺寸。
     */
    minInlineSize: number;
};

/**
 * 使用文本引擎 buffer 测量文本内容。
 *
 * 本入口只消费已解析好的引擎富文本 span 和可选宽度约束；字体加载、
 * asset、fallback 生命周期都由调用方持有。前置条件不满足时会直接抛错，
 * 宿主测量或 span 应用失败时返回 null，不会写回图层宽高。
 *
 * @param engine - TextEngine | null | undefined，已经 ready 的文本引擎实例。
 * @param input - MeasureBufferInput，render adapter 与 buffer 测量共享的文本模型。
 * @returns BufferMeasureResult | null，成功时返回尺寸；失败时返回 null。
 */
export function measureBuffer(
    engine: TextEngine | null | undefined,
    input: MeasureBufferInput,
): BufferMeasureResult | null {
    if (!engine || engine.isDisposed) throw Errors.EngineNotReady;
    if (engine.getRegisteredFontCount() === 0) throw Errors.NoRegisteredFonts;

    // FIXME: 这里 isPositiveFiniteNumber 会不会有点冗余？理论上 UI 传入的数值都是安全的。
    if (!isPositiveFiniteNumber(input.fontSize)) throw Errors.InvalidFontSize;
    if (!isPositiveFiniteNumber(input.lineHeightScale)) throw Errors.InvalidLineHeightScale;

    const isHorizontal = input.writingMode === 'horizontal-tb';
    const inlineSize = isHorizontal ? input.width : input.height;
    if (input.fixedInlineSize && !isPositiveFiniteNumber(inlineSize)) throw Errors.InvalidFixedInlineSize;

    let buffer: TextBuffer | null = null;

    try {
        buffer = engine.createBuffer({
            fontSize: input.fontSize,
            lineHeightScale: input.lineHeightScale,
            letterSpacing: isFiniteNumber(input.letterSpacing) ? input.letterSpacing : 0,
            align: input.align,
        });

        const spans =
            input.spans.length > 0 ? input.spans : [{ text: input.text, attrs: { fontSize: input.fontSize } }];

        buffer.setWritingMode(input.writingMode);
        buffer.setSize(
            input.fixedInlineSize && isHorizontal ? input.width : null,
            input.fixedInlineSize && !isHorizontal ? input.height : null,
        );
        buffer.setRichText(spans);
        buffer.layout();

        const measureResult = measureDelegatedHostRuns(buffer);
        if (!measureResult.ok) {
            return null;
        }

        return {
            width: input.fixedInlineSize && isHorizontal ? input.width! : ceilNonNegative(buffer.getContentWidth()),
            height: input.fixedInlineSize && !isHorizontal ? input.height! : ceilNonNegative(buffer.getContentHeight()),
            minInlineSize: ceilNonNegative(isHorizontal ? buffer.getContentMinWidth() : buffer.getContentMinHeight()),
        };
    } catch (error) {
        console.error(`[TextKit:measureBuffer@${engineVersion}] error`, error);
        return null;
    } finally {
        if (buffer) buffer.dispose();
    }
}
